import { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader2, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import PoLineLink from '../../common/PoLineLink';
import PoHeaderLink from '../../common/PoHeaderLink';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { purchasingAPI } from '../../../lib/api';
import type { POLineSchedule } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';

export type DrilldownMode = 'firm' | 'forecast' | 'all';

export interface ForecastDrilldownFilters {
  /** Customer (always present from the dashboard scope). */
  customer_id?: number | string;
  /** Optional supplier filter set on the dashboard. */
  supplier_id?: number | string;
  /** Optional part-number filter set on the dashboard. */
  part_number?: string;
  /** ERP scope from the parent shell ('4' for scheduling agreements). */
  erp_order_type: '1' | '4';
}

interface ForecastDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  mode: DrilldownMode;
  filters: ForecastDrilldownFilters;
  /** Today's date as ISO string — passed in from the shell so the modal
   *  doesn't re-derive it inconsistently. Schedules with a date earlier
   *  than this are excluded (forward pipeline only). */
  todayIso: string;
  /** Default firm-days fallback for parts with no PartScheduleEntry —
   *  must equal the value the dashboard tile uses (passed in from
   *  ``data.default_firm_days``) so the drilldown count lines up
   *  exactly with the tile's count. */
  defaultFirmDays: number;
}

const PAGE_SIZE = 50;

const MODE_TITLES: Record<DrilldownMode, string> = {
  firm: 'Firm Window — schedules',
  forecast: 'Forecast — schedules beyond firm window',
  all: 'Pipeline — all forward schedules',
};

const MODE_BADGES: Record<DrilldownMode, { label: string; className: string }> = {
  firm: { label: 'Firm', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  forecast: { label: 'Forecast', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  all: { label: 'Forward', className: 'bg-slate-50 text-slate-700 border-slate-200' },
};

/** Resolve the segment badge for one row.
 *
 *  Mode 'firm' / 'forecast' short-circuits because the server-side filter
 *  already pinned every row in the result to the matching segment.
 *
 *  Mode 'all' computes per row using the SAME fallback the dashboard tile
 *  uses (``default_firm_days`` for parts with no PartScheduleEntry). The
 *  serializer's ``is_forecast`` field doesn't know about
 *  ``default_firm_days`` and reports False for unconfigured parts, so
 *  using it directly would mislabel rows the dashboard treats as
 *  forecast.
 */
function rowSegmentBadge(
  row: POLineSchedule,
  mode: DrilldownMode,
  todayIso: string,
  defaultFirmDays: number,
) {
  if (mode === 'firm') return MODE_BADGES.firm;
  if (mode === 'forecast') return MODE_BADGES.forecast;
  // mode === 'all'
  const firmDays = typeof row.firm_days === 'number'
    ? Math.max(row.firm_days, 0)
    : defaultFirmDays;
  if (!row.schedule_date) return MODE_BADGES.firm;
  const todayMs = new Date(todayIso).getTime();
  const scheduleMs = new Date(row.schedule_date).getTime();
  const daysAhead = Math.floor((scheduleMs - todayMs) / 86_400_000);
  return daysAhead > firmDays ? MODE_BADGES.forecast : MODE_BADGES.firm;
}

// Map UI column id → backend `ordering` field. ``null`` means the column
// is rendered as a static (non-sortable) header.
const SORT_FIELDS: Record<string, string | null> = {
  schedule_date: 'schedule_date',
  po_number: 'po_line__po__po_number',
  line_number: 'po_line__line_number',
  schedule_line_number: 'schedule_line_number_int',
  part_number: 'po_line__part_number',
  description: null,
  supplier_name: 'po_line__po__supplier__name',
  // `_firm_days` is annotated by the schedule viewset's _apply_forecast_rules
  // when default_firm_days is in the query params (always true from this modal).
  firm_days: '_firm_days',
  balance_qty: 'balance_qty',
  segment: null,
};

interface ColumnDef {
  id: keyof typeof SORT_FIELDS;
  label: string;
  align?: 'left' | 'right';
  className?: string;
}

const COLUMNS: ColumnDef[] = [
  { id: 'schedule_date', label: 'Schedule Date', align: 'left' },
  { id: 'po_number', label: 'PO #', align: 'left' },
  { id: 'line_number', label: 'Line', align: 'right' },
  { id: 'schedule_line_number', label: 'Sched', align: 'right' },
  { id: 'part_number', label: 'Part', align: 'left' },
  { id: 'description', label: 'Description', align: 'left' },
  { id: 'supplier_name', label: 'Supplier', align: 'left' },
  { id: 'firm_days', label: 'Firm Period', align: 'right' },
  { id: 'balance_qty', label: 'Open Qty', align: 'right' },
  { id: 'segment', label: 'Segment', align: 'left' },
];

/** Build a 1-based numeric pagination spec with ellipsis: shows
 *  current ± 1, plus first / last, plus '…' fillers. e.g.
 *    page=1, total=10  → [1, 2, 3, …, 10]
 *    page=5, total=10  → [1, …, 4, 5, 6, …, 10]
 *    page=10, total=10 → [1, …, 8, 9, 10]
 */
function buildPageList(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: Array<number | '…'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('…');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

export function ForecastDrilldownModal({
  open, onClose, mode, filters, todayIso, defaultFirmDays,
}: ForecastDrilldownModalProps) {
  const { formatDate, formatNumber } = useFormatters();
  const [rows, setRows] = useState<POLineSchedule[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState<string>('1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sort state — column id from COLUMNS plus direction. Default: schedule_date asc.
  const [sortCol, setSortCol] = useState<string>('schedule_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const orderingParam = useMemo(() => {
    const field = SORT_FIELDS[sortCol];
    if (!field) return 'schedule_date';
    return sortDir === 'desc' ? `-${field}` : field;
  }, [sortCol, sortDir]);

  // Single source of truth for the filter contract — list query and CSV
  // export both call this. Counts shown = rows downloaded.
  const buildFilterParams = useCallback((): Record<string, string | number | boolean> => {
    const params: Record<string, string | number | boolean> = {
      schedule_date__gte: todayIso,
      exclude_line_zero: true,
      // The firm/forecast SQL behind these tiles drops cancelled slots
      // (`AND s.status != 'cancelled'` in analytics/views.py): they are retired
      // demand that keeps its schedule_qty but carries no balance, so counting
      // them inflates the schedule count and total_qty while adding nothing to
      // open. Without this the list contradicted the tile it was opened from —
      // firm 6,906 rows against a tile of 5,178, forecast 37,725 against
      // 31,535. The CSV export shares this builder, so it was off by the same
      // 7,918 rows.
      exclude_status: 'cancelled',
      default_firm_days: defaultFirmDays,
    };
    if (filters.customer_id) params['po_line__po__customer'] = filters.customer_id;
    if (filters.supplier_id) params['po_line__po__supplier'] = filters.supplier_id;
    if (filters.part_number) params['po_line__part_number'] = filters.part_number;
    params['po_line__po__erp_order_type'] = filters.erp_order_type;
    if (mode === 'firm') params['is_forecast'] = false;
    if (mode === 'forecast') params['is_forecast'] = true;
    // mode === 'all': no is_forecast filter — full forward pipeline.
    return params;
  }, [
    filters.customer_id, filters.supplier_id, filters.part_number,
    filters.erp_order_type, mode, todayIso, defaultFirmDays,
  ]);

  const fetchPage = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...buildFilterParams(),
        page: targetPage,
        page_size: PAGE_SIZE,
        ordering: orderingParam,
      };
      const res = await purchasingAPI.getSchedules({ ...params, all_suppliers: true });
      setRows(res.data.results ?? []);
      setTotal(res.data.count ?? 0);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load schedules';
      setError(msg);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, orderingParam]);

  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const exportParams = buildFilterParams();
      const qs = new URLSearchParams(
        Object.entries(exportParams)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString();
      const apiClientModule = await import('../../../lib/api/client');
      const resp = await apiClientModule.default.get(
        `/purchasing/schedules/export-xlsx/?${qs}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `forecast-${mode}-${today}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel export ready');
    } catch (err) {
      console.error('Forecast CSV export failed:', err);
      toast.error('Failed to export — try again');
    } finally {
      setExporting(false);
    }
  }, [buildFilterParams, mode]);

  // Reset to page 1 every time the modal opens, the mode/filters change,
  // or the sort changes (sorting always starts from page 1 — otherwise the
  // user lands on a partially-filled stale page).
  useEffect(() => {
    if (!open) return;
    setPage(1);
    setPageInput('1');
    fetchPage(1);
  }, [open, mode, fetchPage]);

  // Refetch when paginating.
  useEffect(() => {
    if (!open || page === 1) return;
    fetchPage(page);
  }, [page, open, fetchPage]);

  // Keep the input box in sync if `page` changed via numeric button.
  useEffect(() => { setPageInput(String(page)); }, [page]);

  const goToPage = useCallback((p: number) => {
    if (p < 1 || p > totalPages || p === page || loading) return;
    setPage(p);
  }, [page, totalPages, loading]);

  const handleHeaderClick = useCallback((colId: string) => {
    if (!SORT_FIELDS[colId]) return;
    if (sortCol === colId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(colId);
      setSortDir('asc');
    }
  }, [sortCol]);

  const sortIcon = (colId: string) => {
    if (!SORT_FIELDS[colId]) return null;
    if (sortCol !== colId) return <ArrowUpDown className="h-3 w-3 text-muted-foreground inline ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 text-primary inline ml-1" />
      : <ArrowDown className="h-3 w-3 text-primary inline ml-1" />;
  };

  const pageButtons = useMemo(() => buildPageList(page, totalPages), [page, totalPages]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <DialogTitle>{MODE_TITLES[mode]}</DialogTitle>
              <DialogDescription>
                {total.toLocaleString()} forward-dated schedule
                {total === 1 ? '' : 's'}
                {filters.part_number ? <> · part <span className="font-mono">{filters.part_number}</span></> : null}
                {filters.supplier_id ? <> · supplier #{filters.supplier_id}</> : null}
              </DialogDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleExport}
              disabled={loading || exporting || total === 0}
              aria-label="Export to Excel"
              className="shrink-0"
            >
              {exporting
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}
              Export to Excel
            </Button>
          </div>
        </DialogHeader>

        {error && (
          <div className="text-sm text-red-600 py-3">{error}</div>
        )}

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No schedules match this drilldown.</div>
        ) : (
          <div className="overflow-auto max-h-[60vh] border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  {COLUMNS.map((c) => {
                    const sortable = SORT_FIELDS[c.id] !== null;
                    const align = c.align === 'right' ? 'text-right' : 'text-left';
                    const cursor = sortable ? 'cursor-pointer hover:bg-slate-100' : '';
                    return (
                      <th
                        key={c.id}
                        className={`px-3 py-2 font-medium text-slate-700 select-none ${align} ${cursor}`}
                        onClick={() => handleHeaderClick(c.id)}
                        aria-sort={
                          sortable && sortCol === c.id
                            ? (sortDir === 'asc' ? 'ascending' : 'descending')
                            : undefined
                        }
                      >
                        {c.label}{sortIcon(c.id)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const segment = rowSegmentBadge(r, mode, todayIso, defaultFirmDays);
                  const firmPeriod = typeof r.firm_days === 'number' && r.firm_days > 0
                    ? `${r.firm_days}d`
                    : <span className="text-muted-foreground">—</span>;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.schedule_date)}</td>
                      {/* CR-1154: PO number links to the PO header; line to the line item. */}
                      <td className="px-3 py-2 font-mono text-xs">
                        <PoHeaderLink poNumber={r.po_number}>{r.po_number}</PoHeaderLink>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <PoLineLink poLineId={r.po_line_id}>{r.line_number}</PoLineLink>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.schedule_line_number}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.part_number}</td>
                      <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={r.description}>{r.description || '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.supplier_name || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{firmPeriod}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(parseFloat(r.balance_qty || '0'))}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={segment.className}>
                          {segment.label}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Button
                size="sm" variant="outline"
                disabled={page <= 1 || loading}
                onClick={() => goToPage(page - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {pageButtons.map((p, idx) => (
                p === '…' ? (
                  <span key={`gap-${idx}`} className="px-2 text-xs text-muted-foreground">…</span>
                ) : (
                  <Button
                    key={p}
                    size="sm"
                    variant={p === page ? 'default' : 'outline'}
                    disabled={loading}
                    onClick={() => goToPage(p)}
                    aria-current={p === page ? 'page' : undefined}
                    className="min-w-[2.25rem]"
                  >
                    {p}
                  </Button>
                )
              ))}
              <Button
                size="sm" variant="outline"
                disabled={page >= totalPages || loading}
                onClick={() => goToPage(page + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <label htmlFor="page-jump">Go to page</label>
              <input
                id="page-jump"
                type="number"
                min={1}
                max={totalPages}
                value={pageInput}
                disabled={loading}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const n = parseInt(pageInput, 10);
                    if (!Number.isNaN(n)) goToPage(Math.max(1, Math.min(totalPages, n)));
                  }
                }}
                onBlur={() => {
                  const n = parseInt(pageInput, 10);
                  if (!Number.isNaN(n)) goToPage(Math.max(1, Math.min(totalPages, n)));
                  else setPageInput(String(page));
                }}
                className="h-7 w-16 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
              <span>of {totalPages.toLocaleString()}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
